/**
 * PLAS Premium Mega Menu v3 – Hover controller + Overlay
 * Enhances <header-menu> elements with hover-to-open behaviour,
 * positions the mega panel, and toggles the overlay backdrop.
 *
 * v3 changes:
 * - RAF-throttled scroll/resize positioning (no layout thrashing)
 * - Focus management: Escape returns focus to trigger
 * - Overlay fade-out animation instead of snap-off
 * - Prevents DetailsDisclosure.onFocusOut conflicts
 * - Debounced resize handler
 */
(function () {
  'use strict';

  var HOVER_DELAY_IN  = 80;
  var HOVER_DELAY_OUT = 300;
  var DESKTOP_BP      = 990;

  var menus, headerWrapper, overlay;
  var rafPending = false;
  var resizeTimer = null;
  var lastActiveSummary = null;

  function isDesktop() {
    return window.innerWidth >= DESKTOP_BP;
  }

  /* ── RAF-throttled panel positioning ── */
  function positionPanels() {
    if (!headerWrapper) return;
    var rect = headerWrapper.getBoundingClientRect();
    document.documentElement.style.setProperty(
      '--mega-panel-top',
      Math.round(rect.bottom) + 'px'
    );
  }

  function requestPositionUpdate() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      positionPanels();
      rafPending = false;
    });
  }

  /* ── Overlay management with fade-out ── */
  function showOverlay() {
    if (!overlay) return;
    overlay.classList.remove('plas-mega-overlay--hiding');
    overlay.classList.add('plas-mega-overlay--visible');
  }

  function hideOverlay() {
    if (!overlay) return;
    if (!overlay.classList.contains('plas-mega-overlay--visible')) return;
    overlay.classList.add('plas-mega-overlay--hiding');
    overlay.classList.remove('plas-mega-overlay--visible');
  }

  function isAnyMenuOpen() {
    if (!menus) return false;
    for (var i = 0; i < menus.length; i++) {
      var d = menus[i].querySelector('details');
      if (d && d.open) return true;
    }
    return false;
  }

  function updateOverlay() {
    if (isAnyMenuOpen()) {
      showOverlay();
    } else {
      hideOverlay();
    }
  }

  /* ── Menu open/close ── */
  function openMenu(menu) {
    var details = menu.querySelector('details');
    if (!details || details.open) return;
    details.setAttribute('open', '');
    var summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
      lastActiveSummary = summary;
    }
    // Prevent DetailsDisclosure focusout from closing
    menu._plasHoverActive = true;
    updateOverlay();
  }

  function closeMenu(menu) {
    var details = menu.querySelector('details');
    if (!details || !details.open) return;
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    menu._plasHoverActive = false;
    updateOverlay();
  }

  function closeAllMenus(returnFocus) {
    if (!menus) return;
    menus.forEach(function (m) { closeMenu(m); });
    hideOverlay();
    if (returnFocus && lastActiveSummary) {
      lastActiveSummary.focus();
      lastActiveSummary = null;
    }
  }

  /* ── Hover setup per menu item ── */
  function setupHover(menu) {
    var openTimer  = null;
    var closeTimer = null;

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    }

    menu.addEventListener('mouseenter', function () {
      if (!isDesktop()) return;
      clearTimers();
      menus.forEach(function (m) {
        if (m !== menu) closeMenu(m);
      });
      openTimer = setTimeout(function () {
        requestPositionUpdate();
        openMenu(menu);
      }, HOVER_DELAY_IN);
    });

    menu.addEventListener('mouseleave', function () {
      if (!isDesktop()) return;
      clearTimers();
      closeTimer = setTimeout(function () {
        closeMenu(menu);
      }, HOVER_DELAY_OUT);
    });

    // Keep open while hovering over the panel
    var panel = menu.querySelector('.plas-mega-panel');
    if (panel) {
      panel.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        clearTimeout(closeTimer);
      });
      panel.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        clearTimers();
        closeTimer = setTimeout(function () {
          closeMenu(menu);
        }, HOVER_DELAY_OUT);
      });
    }

    // On click summary → toggle (for keyboard / touch on desktop)
    var summary = menu.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', function (e) {
        if (!isDesktop()) return;
        e.preventDefault();
        var details = menu.querySelector('details');
        if (details && details.open) {
          closeMenu(menu);
        } else {
          menus.forEach(function (m) {
            if (m !== menu) closeMenu(m);
          });
          requestPositionUpdate();
          openMenu(menu);
        }
      });
    }

    // Prevent DetailsDisclosure focusout while hover is active
    menu.addEventListener('focusout', function (e) {
      if (menu._plasHoverActive && isDesktop()) {
        e.stopImmediatePropagation();
      }
    }, true);
  }

  /* ── Init ── */
  function init() {
    headerWrapper = document.querySelector('.header-wrapper');
    overlay       = document.getElementById('PlasMegaOverlay');
    menus         = document.querySelectorAll('.plas-mega-nav header-menu');

    if (!menus.length) return;

    menus.forEach(setupHover);

    positionPanels();

    // RAF-throttled scroll
    window.addEventListener('scroll', requestPositionUpdate, { passive: true });

    // Debounced resize
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        positionPanels();
        if (!isDesktop()) closeAllMenus(false);
      }, 100);
    });

    // Overlay click → close all
    if (overlay) {
      overlay.addEventListener('click', function () { closeAllMenus(false); });
    }

    // Click outside → close
    document.addEventListener('click', function (e) {
      if (!isDesktop()) return;
      var target = e.target;
      if (!target.closest('.plas-mega-nav') && !target.closest('.plas-mega-panel') && !target.closest('.plas-mega-overlay')) {
        closeAllMenus(false);
      }
    });

    // Escape → close + return focus
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllMenus(true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
